import { useContext } from 'react'

import { digObject } from '@domain-group/javascript-utils'

import {
  Button, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'
import type { SidebarModalProps } from '@domain-group/suit-theme'

import EntitySelector from '@components/EntitySelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import ProductSelector from '@components/ProductSelector'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import useProductSelector from '@components/ProductSelector/useProductSelector'

import useCurrentUser from '@hooks/useCurrentUser'

import EditOrderBundleItem from './EditOrderBundleItem'
import useCreateOrEditOrderBundleItemModal from './hooks/useCreateOrEditOrderBundleItemModal'

type ModalCallbacks = {
  closeModal: () => void,
  createOrderBundleItem: (payload: HandleCallbackActionParams) => Promise<object>,
  updateOrderBundleItem: (payload: HandleCallbackActionParams) => Promise<object>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
}

const CreateOrEditOrderBundleItemModal = (props: SidebarModalProps & ModalProps) => {
  const { callbacks, modalKey = 'CreateOrEditOrderBundleItemModal', showModal } = props
  const { closeModal, createOrderBundleItem, updateOrderBundleItem } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { orderBundleItem } = modalPayload

  const productSelectorPayload = useProductSelector()
  const { state } = productSelectorPayload

  const {
    callbacks: {
      createOrderBundleItem: createFn,
      mapStateToEntityState,
      updateOrderBundleItem: updateFn,
    },
    isNewRecord,
    loading,
    orderBundleItemForm,
    saveEnabled,
  // @ts-expect-error TS2322
  } = useCreateOrEditOrderBundleItemModal({ orderBundleItem, state })

  const {
    entityState,
    entityState: {
      provider_id,
    },
    setEntityState,
  } = orderBundleItemForm

  const createOrderBundleItemPayload = {
    callbacks: {
      action: isNewRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: isNewRecord ? mapStateToEntityState() : entityState,
    toastText: `Product ${isNewRecord ? 'Added' : 'Updated'}`,
    requestOptions: defaultRequestOptions.orderBundleItem,
  }

  const { isProviderUser } = useCurrentUser()

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={isNewRecord ? 'Add' : 'Edit'} titleSecondLine="Product" />

      <SidebarModal.Content>
        {isNewRecord && (
          <>
            {!isProviderUser && (
              <EntitySelector
                boxProps={{ marginTop: ['large', 0], marginBottom: 'large' }}
                callbacks={{
                  selectItem: provider => setEntityState({ provider_id: provider ? provider.id : null }),
                }}
                entityKey="providers"
                label="* Provider"
                selectedItemId={provider_id}
              />
            )}

            {!!provider_id && (
              <ProductSelector
                productSelectorPayload={productSelectorPayload}
                providerId={provider_id}
              />
            )}
          </>
        )}

        {!isNewRecord && (
          <EditOrderBundleItem orderBundleItemForm={orderBundleItemForm} />
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={loading}
          onClick={
            isNewRecord
              // @ts-expect-error TS2345
              ? () => createOrderBundleItem(createOrderBundleItemPayload)
              // @ts-expect-error TS2345
              : () => updateOrderBundleItem(createOrderBundleItemPayload)
          }
          size="large"
        >
          {saveEnabled ? `${isNewRecord ? 'Create' : 'Update'} Product` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: SidebarModalProps & ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditOrderBundleItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
